import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";
import DataTable from "components/ui/Tables/DataTable";
import { request } from "app/network";
import { useMaterialUIController } from "context";
import SurgerySiteModal from "./SurgerySiteModal";

function SurgerySiteListScreen() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const queryClient = useQueryClient();
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedSite, setSelectedSite] = useState<any | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [siteToDelete, setSiteToDelete] = useState<any | null>(null);

    const { data: pagedData, isLoading } = useQuery({
        queryKey: ["SurgerySite.pagedList"],
        queryFn: async () => {
            const response = await request("SurgerySite.pagedList", {
                paramz: {
                    PageNumber: 1,
                    PageSize: 1000,
                },
            });
            return response.data;
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (seqId: number) => {
            await request("SurgerySite.delete", {
                pathParams: { seqId: seqId.toString() },
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["SurgerySite.pagedList"] });
            setDeleteDialogOpen(false);
            setSiteToDelete(null);
        },
        onError: (error: any) => {
            alert(error?.response?.data?.message || "삭제에 실패했습니다.");
        }
    });

    const handleCreate = () => {
        setSelectedSite(null);
        setModalOpen(true);
    };

    const handleEdit = (site: any) => {
        setSelectedSite(site);
        setModalOpen(true);
    };

    const handleDeleteClick = (site: any) => {
        setSiteToDelete(site);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = () => {
        if (siteToDelete) {
            deleteMutation.mutate(siteToDelete.seqId);
        }
    };

    const handleModalSuccess = () => {
        setModalOpen(false);
        setSelectedSite(null);
        queryClient.invalidateQueries({ queryKey: ["SurgerySite.pagedList"] });
    };

    const columns = [
        { Header: "SEQ", accessor: "seqId", width: "10%" },
        { Header: "수술부위 코드", accessor: "siteCode", width: "20%" },
        { Header: "수술부위 이름", accessor: "siteName", width: "30%" },
        { Header: "코멘트", accessor: "comment", width: "30%" },
        {
            Header: "관리",
            accessor: "actions",
            width: "10%",
            Cell: ({ row }: any) => (
                <MDBox display="flex" alignItems="center">
                    <IconButton
                        size="small"
                        onClick={() => handleEdit(row.original)}
                        sx={{
                            color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                            '&:hover': {
                                backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                                color: '#49a3f1',
                            },
                        }}
                    >
                        <Icon fontSize="small">edit</Icon>
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(row.original)}
                        sx={{
                            color: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                            '&:hover': {
                                backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                                color: '#f44336',
                            },
                        }}
                    >
                        <Icon fontSize="small">delete</Icon>
                    </IconButton>
                </MDBox>
            ),
        },
    ];

    const rows = (pagedData as any)?.items || [];

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <MDBox mb={3}>
                    <Card>
                        <MDBox p={3} lineHeight={1}>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                <MDBox display="flex" alignItems="center">
                                    <Icon fontSize="medium">healing</Icon>
                                    <MDTypography variant="h5" fontWeight="medium" ml={1}>
                                        수술부위 관리
                                    </MDTypography>
                                </MDBox>
                                <MDButton variant="gradient" color="info" onClick={handleCreate}>
                                    <Icon fontSize="small" sx={{ mr: 0.5 }}>add</Icon>
                                    수술부위 등록
                                </MDButton>
                            </MDBox>
                            <MDTypography variant="button" color="text">
                                수술부위 목록 조회 및 관리
                            </MDTypography>
                        </MDBox>



                        <DataTable
                            table={{ columns, rows }}
                            showTotalEntries={true}
                            isSorted={false}
                            noEndBorder
                            entriesPerPage={false}
                        />
                    </Card>
                </MDBox>
            </MDBox>
            <Footer />

            <SurgerySiteModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSuccess={handleModalSuccess}
                surgerySite={selectedSite}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>수술부위 삭제</DialogTitle>
                <DialogContent>
                    <MDTypography variant="body2">
                        정말로 삭제하시겠습니까?
                        <br />
                        <strong>{siteToDelete?.siteName}</strong> ({siteToDelete?.siteCode})
                    </MDTypography>
                </DialogContent>
                <DialogActions>
                    <MDButton onClick={() => setDeleteDialogOpen(false)} variant="outlined" color="dark">
                        취소
                    </MDButton>
                    <MDButton onClick={confirmDelete} variant="gradient" color="error">
                        삭제
                    </MDButton>
                </DialogActions>
            </Dialog>

        </DashboardLayout>
    );
}

export default SurgerySiteListScreen;
